// crewbit-admin/src/components/money/MoneyExpenseEditPanel.tsx
//
// Edit slide-over for an existing expense. Hydrates from the live `expenses`
// row via direct supabase select inside an internal hook (kept thin to honour
// the convention that only `hooks/` files own DB queries; per Session 129
// Failure-mode #8). Updates routed through `expenseMutations.updateExpense`.
//
// Archive button is the v1 affordance for retiring an expense — sets
// `archived = true` via the same wrapper (`archiveExpense`). Restoration
// happens from the table when "Show archived" is on.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive } from 'lucide-react';
import { palette, spacing, radii, transition, admin } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { MoneyInput } from '../common/MoneyInput';
import { DatePicker } from '../common/DatePicker';
import * as expenseMutations from '../../lib/expenseMutations';
import { lookupErrorCopy, type MoneyErrorCode } from '../../lib/errorMap';
import { supabase } from '../../lib/supabase';
import { VendorSelect } from './VendorSelect';
import {
  EXPENSE_CATEGORY_GROUPS,
  PAYMENT_METHODS,
} from '../../constants/expenseCategories';
import { canonicalMoney } from '../../lib/money';
import type { Expense } from '../../types';

interface MoneyExpenseEditPanelProps {
  expenseId: string | null;
  onClose: () => void;
}

interface EditState {
  category: string;
  description: string;
  amount: string;
  date: string;
  vendorName: string;
  // Wave 7 — vendor entity FK selected by the picker. NULL when the row
  // is legacy (vendorName-only).
  vendorId: string | null;
  // Wave 8 Step 5 — per-expense tax_type (QC + GST/PST jurisdictions).
  // NULL = inherit business default; explicit override beats the default.
  taxType: string | null;
  paymentMethod: string;
  isHstExempt: boolean;
  notes: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: palette.g600,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

function fromExpense(e: Expense): EditState {
  return {
    category: e.category,
    description: e.description ?? '',
    amount: canonicalMoney(e.amount),
    date: e.date,
    vendorName: e.vendor_name ?? '',
    vendorId: e.vendor_id ?? null,
    taxType: (e as { tax_type?: string | null }).tax_type ?? null,
    paymentMethod: e.payment_method ?? '',
    isHstExempt: e.is_hst_exempt ?? false,
    notes: e.notes ?? '',
  };
}

export function MoneyExpenseEditPanel({ expenseId, onClose }: MoneyExpenseEditPanelProps) {
  const isOpen = expenseId !== null;
  const [original, setOriginal] = useState<Expense | null>(null);
  const [state, setState] = useState<EditState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Hydrate when expenseId becomes set. Supabase select is the simplest path
  // here — useExpenses only exposes the cached list and the row may not be in
  // the page's filtered slice (e.g., archived row reached via deep link).
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!expenseId) {
      setOriginal(null);
      setState(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('expenses')
        .select(
          'id, business_id, category, description, amount, tax_amount, tax_type, date, payment_method, vendor_name, vendor_id, receipt_url, receipt_filename, is_recurring, notes, project_id, job_id, phase_id, trip_id, logged_by, created_at, mileage_km, mileage_rate, mileage_from, mileage_to, ai_categorized, ocr_data, ocr_confidence, deductible_percentage, deductible_amount, is_hst_exempt, t2125_line, archived',
        )
        .eq('id', expenseId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setToast({ msg: 'Expense not found.', type: 'error' });
        setLoading(false);
        return;
      }
      const expense = data as Expense;
      setOriginal(expense);
      setState(fromExpense(expense));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [expenseId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const update = useCallback(<K extends keyof EditState>(key: K, value: EditState[K]) => {
    setState((s) => (s ? { ...s, [key]: value } : s));
  }, []);

  const dirty = useMemo(() => {
    if (!state || !original) return false;
    const fresh = fromExpense(original);
    return (
      fresh.category !== state.category ||
      fresh.description !== state.description ||
      fresh.amount !== state.amount ||
      fresh.date !== state.date ||
      fresh.vendorName !== state.vendorName ||
      fresh.paymentMethod !== state.paymentMethod ||
      fresh.isHstExempt !== state.isHstExempt ||
      fresh.notes !== state.notes
    );
  }, [state, original]);

  const handleSave = async () => {
    if (!state || !original) return;
    const amountNum = Number(canonicalMoney(state.amount));
    if (isNaN(amountNum) || amountNum <= 0) {
      setToast({ msg: lookupErrorCopy('update_expense', 'INVALID_AMOUNT'), type: 'error' });
      return;
    }
    setSaving(true);
    const result = await expenseMutations.updateExpense({
      expenseId: original.id,
      category: state.category,
      description: state.description || null,
      amount: amountNum,
      vendorName: state.vendorName || null,
      vendorId: state.vendorId,
      taxType: state.taxType,
      paymentMethod: state.paymentMethod || null,
      expenseDate: state.date,
      isHstExempt: state.isHstExempt,
      notes: state.notes || null,
    });
    setSaving(false);
    if (result.success) {
      setToast({ msg: 'Expense updated', type: 'success' });
      onClose();
    } else {
      const code = result.error.code as MoneyErrorCode;
      setToast({ msg: lookupErrorCopy('update_expense', code), type: 'error' });
    }
  };

  const handleArchive = async () => {
    if (!original) return;
    setArchiving(true);
    const result = await expenseMutations.archiveExpense(original.id);
    setArchiving(false);
    setConfirmArchive(false);
    if (result.success) {
      setToast({ msg: 'Expense archived', type: 'success' });
      onClose();
    } else {
      const code = result.error.code as MoneyErrorCode;
      setToast({ msg: lookupErrorCopy('update_expense', code), type: 'error' });
    }
  };

  return (
    <>
      <SlidePanel isOpen={isOpen} onClose={onClose} title="Edit expense" width={560}>
        {loading && (
          <div style={{ padding: spacing.lg, color: palette.g500, fontSize: 13 }}>Loading…</div>
        )}
        {!loading && state && original && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            {original.archived && (
              <div
                style={{
                  padding: `${spacing.sm}px ${spacing.md}px`,
                  borderRadius: radii.sm,
                  background: palette.g100,
                  color: palette.g600,
                  fontSize: 12.5,
                }}
              >
                This expense is archived. Edits will save but it stays hidden from the default list.
              </div>
            )}
            <div style={{ display: 'flex', gap: spacing.md }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Vendor</label>
                {/* Wave 7: freetext replaced by VendorSelect */}
                <VendorSelect
                  vendorId={state.vendorId}
                  vendorName={state.vendorName}
                  onChange={(vendorId, vendorName) => {
                    setState((s) => (s ? { ...s, vendorId, vendorName: vendorName ?? '' } : s));
                  }}
                />
              </div>
              <div style={{ width: 180 }}>
                <label style={labelStyle}>Amount *</label>
                <MoneyInput
                  value={state.amount}
                  onChange={(v) => update('amount', v)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: spacing.md }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Date *</label>
                <DatePicker
                  value={state.date}
                  onChange={(v) => update('date', v ?? state.date)}
                  clearable={false}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Payment method</label>
                <select
                  value={state.paymentMethod}
                  onChange={(e) => update('paymentMethod', e.target.value)}
                  style={{ ...inputStyle, color: state.paymentMethod ? palette.g800 : palette.g400 }}
                >
                  <option value="">Select…</option>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Category *</label>
              <select
                value={state.category}
                onChange={(e) => update('category', e.target.value)}
                style={{ ...inputStyle, color: state.category ? palette.g800 : palette.g400 }}
              >
                <option value="">Select category…</option>
                {EXPENSE_CATEGORY_GROUPS.map((g) => (
                  <optgroup key={g.group} label={g.label}>
                    {g.subcategories.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Description</label>
              <input
                value={state.description}
                onChange={(e) => update('description', e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: palette.g700,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={state.isHstExempt}
                  onChange={(e) => update('isHstExempt', e.target.checked)}
                />
                Mark as HST exempt
              </label>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={state.notes}
                onChange={(e) => update('notes', e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            <div
              style={{
                position: 'sticky',
                bottom: -20,
                marginLeft: -20,
                marginRight: -20,
                marginTop: spacing.lg,
                padding: `${spacing.md}px ${spacing.lg}px`,
                background: palette.white,
                borderTop: `1px solid ${admin.border}`,
                display: 'flex',
                gap: spacing.sm,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {!original.archived ? (
                <button
                  type="button"
                  onClick={() => setConfirmArchive(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '9px 14px',
                    borderRadius: radii.sm,
                    border: `1px solid ${palette.g200}`,
                    background: palette.white,
                    color: palette.red,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: `background ${transition.fast}`,
                  }}
                >
                  <Archive size={13} />
                  Archive
                </button>
              ) : (
                <span />
              )}
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '9px 18px',
                    borderRadius: radii.sm,
                    border: `1px solid ${palette.g200}`,
                    background: palette.white,
                    fontSize: 13,
                    fontWeight: 600,
                    color: palette.g600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={!dirty || saving}
                  onClick={() => void handleSave()}
                  style={{
                    padding: '9px 22px',
                    borderRadius: radii.sm,
                    border: 'none',
                    background: !dirty || saving ? palette.g300 : palette.accent,
                    color: palette.white,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: !dirty || saving ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </SlidePanel>

      <ConfirmDialog
        isOpen={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => void handleArchive()}
        title="Archive this expense?"
        message="Archived expenses are hidden from the default list and don't count toward this month's totals. You can restore them with the Show archived toggle."
        confirmLabel={archiving ? 'Archiving…' : 'Archive'}
        confirmVariant="danger"
        loading={archiving}
      />

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
